import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';
import {DefaultMap} from 'external/dev_april_corgi+/js/common/collections';

import {Action, Result} from '../types/actions';
import {
  DataValue,
  MaterialId,
  MaterialValue,
  PlateId,
  WellId,
} from '../types/data';

import {ActionsHandler} from './actions_handler';
import {PipeId} from './graph';
import {
  Instruction,
  LoadLabwareInstruction,
  LoadMaterialInstruction,
  StoreMaterialInstruction,
} from './opentrons';
import {Well} from './opentrons_deck';
import {
  OpentronsActionsHandler,
  OpentronsExecutor,
} from './opentrons_actions_handler';
import {OpentronsFlexRobot} from './robots';

export interface WellProfile {
  changes: Array<{
    kind: 'add' | 'load' | 'remove' | 'store';
    pipe: PipeId;
    volumeNl: number;
  }>;
  finalVolumeNl: number;
  materials: Map<MaterialId, MaterialValue>;
  maxVolumeNl: number;
  actuals: DefaultMap<
    Well,
    {
      plate: number;
      position: {row: number; col: number};
      loadedVolumeNl: number;
      storedVolumeNl: number;
    }
  >;
}

export interface PlateProfile {
  layouts: Array<{rows: number; columns: number}>;
  maxWellVolumeNl: number;
  wells: DefaultMap<WellId, WellProfile>;
}

interface TipProfile {
  droppedInRack: number;
  droppedInTrash: number;
  pickedUp: number;
}

export interface Profile {
  readonly plateProfiles: Map<PlateId, PlateProfile>;
  readonly tipProfile: Readonly<TipProfile>;
}

export class ProfilingActionsHandler implements ActionsHandler {
  static makeNaiveProfiler(robot: OpentronsFlexRobot): ProfilingActionsHandler {
    const listener = new ListeningExecutor(/* inner= */ undefined);
    return new ProfilingActionsHandler(
      new OpentronsActionsHandler(
        listener,
        /* guide= */ undefined,
        robot,
        /* sandbox= */ true,
      ),
      listener,
    );
  }

  private readonly plateProfiles: Map<PlateId, PlateProfile>;

  constructor(
    private readonly inner: OpentronsActionsHandler,
    private readonly listener: ListeningExecutor,
  ) {
    this.plateProfiles = new Map();
  }

  getProfile(): Profile {
    return {
      plateProfiles: this.plateProfiles,
      tipProfile: this.listener.tipProfile,
    };
  }

  async start(): Promise<void> {
    await this.inner.start();
  }

  async execute(pipe: PipeId, actions: Action[]): Promise<Result[]> {
    const results = await this.inner.execute(pipe, actions);
    for (let i = 0; i < actions.length; ++i) {
      const action = actions[i];
      const result = results[i];
      if (action.kind === 'load_material') {
        if (result.kind !== 'load_material') {
          throw new Error('Expected a load_material result');
        }

        const to = action.into;
        const well = checkExists(this.plateProfiles.get(to.plate)).wells.get(
          to.well,
        );
        well.changes.push({
          kind: 'load',
          pipe,
          volumeNl: action.volumeNl,
        });
        well.finalVolumeNl += action.volumeNl;
        well.materials.set(action.material.id, action.material);
        well.maxVolumeNl = Math.max(well.finalVolumeNl, well.maxVolumeNl);
        const plateGroup = this.inner.plateGroups.get(to.plate);
        const wellGroup = plateGroup?.wells.get(to.well);
        for (const actual of result.wells) {
          const found = wellGroup?.wells.find(
            w => w.plate.bay === actual.bay && w.well.slot === actual.well,
          );
          if (!plateGroup || !found) {
            continue;
          }

          const profile = well.actuals.get(found.well);
          profile.plate = plateGroup.plates.indexOf(found.plate);
          profile.position = found.well.position;
          profile.loadedVolumeNl += actual.volumeNl;
        }
      } else if (action.kind === 'load_plate') {
        if (result.kind !== 'load_plate') {
          throw new Error('Expected a load_plate result');
        }

        this.plateProfiles.set(action.id, {
          layouts: result.layouts,
          maxWellVolumeNl: result.maxWellVolumeNl,
          wells: new DefaultMap(() => ({
            changes: [],
            finalVolumeNl: 0,
            materials: new Map(),
            maxVolumeNl: 0,
            actuals: new DefaultMap(() => ({
              plate: -1,
              position: {row: -1, col: -1},
              loadedVolumeNl: 0,
              storedVolumeNl: 0,
            })),
          })),
        });
      } else if (action.kind === 'pipette') {
        for (const movement of action.movements) {
          const from = movement.from;
          const fromWell = checkExists(
            this.plateProfiles.get(from.plate),
          ).wells.get(from.well);
          fromWell.changes.push({
            kind: 'remove',
            pipe,
            volumeNl: movement.volumeNl,
          });
          fromWell.finalVolumeNl -= movement.volumeNl;
          const to = movement.to;
          const toWell = checkExists(
            this.plateProfiles.get(to.plate),
          ).wells.get(to.well);
          toWell.changes.push({
            kind: 'add',
            pipe,
            volumeNl: movement.volumeNl,
          });
          toWell.finalVolumeNl += movement.volumeNl;
          toWell.maxVolumeNl = Math.max(
            toWell.finalVolumeNl,
            toWell.maxVolumeNl,
          );
        }
      } else if (action.kind === 'store_material') {
        if (result.kind !== 'store_material') {
          throw new Error('Expected a store_material result');
        }

        const from = action.from;
        const well = checkExists(this.plateProfiles.get(from.plate)).wells.get(
          from.well,
        );
        well.changes.push({
          kind: 'store',
          pipe,
          volumeNl: action.volumeNl,
        });
        well.finalVolumeNl -= action.volumeNl;
        well.materials.set(action.material.id, action.material);
        const plateGroup = this.inner.plateGroups.get(from.plate);
        const wellGroup = plateGroup?.wells.get(from.well);
        for (const actual of result.wells) {
          const found = wellGroup?.wells.find(
            w => w.plate.bay === actual.bay && w.well.slot === actual.well,
          );
          if (!plateGroup || !found) {
            continue;
          }

          const profile = well.actuals.get(found.well);
          profile.plate = plateGroup.plates.indexOf(found.plate);
          profile.position = found.well.position;
          profile.storedVolumeNl += actual.volumeNl;
        }
      }
    }
    return results;
  }

  async pipeFinished(
    pipe: PipeId,
    value: DataValue | undefined,
  ): Promise<void> {
    await this.inner.pipeFinished(pipe, value);
  }

  async finish(): Promise<void> {
    await this.inner.finish();
  }
}

export class ListeningExecutor implements OpentronsExecutor {
  tipProfile: TipProfile;

  constructor(private readonly inner: OpentronsExecutor | undefined) {
    this.tipProfile = {
      droppedInRack: 0,
      droppedInTrash: 0,
      pickedUp: 0,
    };
  }

  async create(): Promise<void> {
    await this.inner?.create();
  }

  async setup(
    pipe: PipeId,
    instruction:
      | LoadLabwareInstruction
      | LoadMaterialInstruction
      | StoreMaterialInstruction,
  ): Promise<void> {
    await this.inner?.setup(pipe, instruction);
  }

  async execute(pipe: PipeId, instructions: Instruction[]): Promise<void> {
    await this.inner?.execute(pipe, instructions);
    for (const instruction of instructions) {
      if (instruction.kind === 'drop_tip') {
        if (instruction.well) {
          this.tipProfile.droppedInRack += instruction.channels;
        } else {
          this.tipProfile.droppedInTrash += instruction.channels;
        }
      } else if (instruction.kind === 'load_labware') {
      } else if (instruction.kind === 'pick_up_tip') {
        this.tipProfile.pickedUp += instruction.channels;
      }
    }
  }

  async pipeFinished(
    pipe: PipeId,
    value: DataValue | undefined,
  ): Promise<void> {
    await this.inner?.pipeFinished(pipe, value);
  }

  async finish(): Promise<void> {
    await this.inner?.finish();
  }
}
